import React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '@/components/errorBoundary';
import { PATH } from '@/router/PATH';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { RateHistory } from './components/rateHistory';
import { RatesOnDate } from './components/ratesOnDate';
import { SettingsNotice } from './components/settingsNotice';
import { SettingsPageView } from './SettingsPageView';
import { useSettingsPage } from './useSettingsPage';

/**
 * `/accounting/settings` — the entity's tax regime and the rates every ledger
 * calculation reads, as a versioned, effective-dated timeline.
 *
 * Owns its data hook and an `ErrorBoundary`; the throw lives in the inner
 * Content child, since React does not catch a component's own throws (the
 * repo's self-served page pattern).
 *
 * Three states replace the page rather than erroring into the boundary, because
 * none of them is a fault: a kiosk session (which would 403 on every request), a
 * role that may not read, and a tenant with no accounting period (whose rates
 * simply have not been seeded yet).
 */
export const SettingsPage: React.FC = () => {
    const page = useSettingsPage();
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            errorTitle: t('settings.error.title'),
            errorMessage: t('settings.error.message'),
            errorRetry: t('settings.error.retry'),
        }),
        [t],
    );

    return (
        <div
            className="flex w-full flex-col gap-8"
            {...getNodeTestIdProp('settings-page')}
        >
            <ErrorBoundary
                data-testid="settings-error-boundary"
                fallbackMessage={copy.errorMessage}
                id="settings-error-boundary"
                onRetry={page.onRetry}
                resetKeys={[page.error]}
                retryLabel={copy.errorRetry}
                title={copy.errorTitle}
            >
                <SettingsPageContent page={page} />
            </ErrorBoundary>
        </div>
    );
};

type SettingsPageContentProps = {
    page: ReturnType<typeof useSettingsPage>;
};

const SettingsPageContent: React.FC<SettingsPageContentProps> = ({ page }) => {
    const { t } = useTranslation('pages');

    if (page.error) {
        throw page.error;
    }

    // A PIN-issued kiosk token 403s on every tax-settings endpoint whatever the
    // role holds, so explain the requirement rather than rendering a page whose
    // every request fails.
    if (page.access.kind === 'kiosk') {
        return (
            <SettingsNotice
                body={t('settings.kiosk.body')}
                icon="gravity-ui:lock"
                testId="settings-kiosk-notice"
                title={t('settings.kiosk.title')}
                tone="warning"
            />
        );
    }

    if (page.access.kind === 'forbidden') {
        return (
            <SettingsNotice
                body={t('settings.forbidden.body')}
                icon="gravity-ui:lock"
                testId="settings-forbidden-notice"
                title={t('settings.forbidden.title')}
                tone="warning"
            />
        );
    }

    // No set has ever been recorded, because the seeder waits for an accounting
    // period. That is an onboarding gap, not an error — and one the user can
    // close themselves.
    if (page.status === 'onboarding') {
        return (
            <SettingsNotice
                action={{
                    label: t('settings.onboarding.action'),
                    // The Periods **tab**, one click away in this same shell —
                    // not /accounting/periods. This is a fresh tenant's first
                    // run, so it is the likeliest path out of Accounting and
                    // the one that must not lead there.
                    to: PATH.ACCOUNTING.CONFIGURATIONS.PERIODS,
                }}
                body={t('settings.onboarding.body')}
                icon="gravity-ui:calendar"
                testId="settings-onboarding-notice"
                title={t('settings.onboarding.title')}
                tone="info"
            />
        );
    }

    // The View keeps its headings, field labels and CTAs while loading and just
    // disables the inputs — the repo's loading-state standard.
    return (
        <>
            <SettingsPageView isLoading={page.isLoading} page={page} />
            <RatesOnDate />
            <RateHistory />
        </>
    );
};
